import {
  Component,
  ElementRef,
  ViewEncapsulation,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
  DestroyRef,
  HostListener,
  effect,
  untracked,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Viewer, PanoData } from '@photo-sphere-viewer/core';
import { EquirectangularVideoAdapter } from '@photo-sphere-viewer/equirectangular-video-adapter';
import { VideoPlugin } from '@photo-sphere-viewer/video-plugin';

import '@photo-sphere-viewer/core/index.css';
import { PanoRecorder, RecordingFormat } from './pano-recorder';

const RECORDING_FORMATS: ReadonlyArray<RecordingFormat> = ['16:9', '9:16', '4:5', '1:1'];

interface Point {
  x: number;
  y: number;
}

interface DrawnStroke {
  points: Point[];
  completedAt: number | null; 
}

@Component({
  selector: 'app-sphere-video-player',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './pano-video-player.component.html',
  styleUrls: ['./pano-video-player.component.scss'],
})
export class SphereVideoPlayerComponent {
  // --- Inputs ---
  videoUrl = input.required<string>();
  panoData = input.required<PanoData>();
  recordingStartTime = input.required<string | Date>();
  startTimeSec = input<number>(0);
  fps = input<number | null>(null);
  timeUpdate = output<number>();

  // --- View References ---
  playerWrapperRef = viewChild.required<ElementRef<HTMLDivElement>>('playerWrapper');
  viewerContainerRef = viewChild.required<ElementRef<HTMLDivElement>>('viewerContainer');
  seekBarRef = viewChild.required<ElementRef<HTMLInputElement>>('seekBar');
  canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('drawingCanvas');
  previewContainerRef = viewChild<ElementRef<HTMLDivElement>>('previewContainer');

  // --- State Signals ---
  isPlaying = signal(false);
  progressPercentage = signal(0);
  currentTimeSec = signal(0);
  totalDuration = signal(0);
  isUserActive = signal(true);
  isFullscreen = signal(false);
  volume = signal(0);
  isMuted = signal(true);

  // --- Drawing State ---
  isDrawMode = signal(false);
  hasDrawings = signal(false);

  // --- Recording State ---
  readonly recordingFormats = RECORDING_FORMATS;
  isRecording = signal(false);
  showRecordMenu = signal(false);
  recordingFormat = signal<RecordingFormat | null>(null);
  recordingElapsedSec = signal(0);
  private recorder: PanoRecorder | null = null;
  private recordingTickInterval: any = null;
  // Wall-clock timestamp (Date.now()) when the current recording started.
  private recordingStartedAtMs = 0;
  private recordingRafHandle: number | null = null;
  private recordingFrameDriverActive = false;
  // Guards the await gap inside startRecording (see the comment there).
  private recordingStarting = false;
  
  // Rendering & Stroke Management
  private ctx: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  private strokes: DrawnStroke[] = [];
  private currentStroke: DrawnStroke | null = null;
  private rafId: number | null = null;
  
  private readonly FADE_DELAY_MS = 4000;
  private readonly FADE_DURATION_MS = 2000;
  private readonly DEFAULT_PITCH = -0.5;

  // --- Scrubber Signals ---
  isScrubbing = signal(false);
  showPreview = signal(false);
  previewLeftPx = signal(0);
  previewTime = signal<Date | null>(null);
  seekFeedbackDirection = signal<'backward' | 'forward' | null>(null);
  seekFeedbackSeconds = signal(0);
  private readonly PREVIEW_PREF_KEY = 'pano.scrubPreviewEnabled';
  isCoarsePointer = (typeof window !== 'undefined' && typeof window.matchMedia === 'function')
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
  previewEnabled = signal(this.isCoarsePointer ? false : this.loadPreviewPref());

  private loadPreviewPref(): boolean {
    try {
      const v = localStorage.getItem(this.PREVIEW_PREF_KEY);
      if (v === null) return true;
      return v === 'true';
    } catch {
      return true;
    }
  }

  private savePreviewPref(value: boolean) {
    try { localStorage.setItem(this.PREVIEW_PREF_KEY, String(value)); } catch {}
  }
  showSettings = signal(false);

  private previewViewer: Viewer | null = null;
  private previewVideoPlugin: any;
  private previewSeekTimeout: any = null;
  private pendingPreviewSec: number | null = null;

  // --- Playback State Management ---
  private playbackModifier: 'none' | 'fast' | 'slow' | 'reverse' = 'none';
  private rewindInterval: any = null;
  private wasPlayingBeforeRewind = false;
  private arrowHoldTimeout: any = null;
  private heldArrowKey: 'ArrowLeft' | 'ArrowRight' | null = null;
  private actionStartTime = 0;
  private wasPlayingBeforeAction = false;
  private readonly TAP_THRESHOLD_MS = 200;
  private seekFeedbackTimeout: any = null;
  private touchTapResetTimeout: any = null;
  private touchTapCount = 0;
  private lastTouchTapAt = 0;
  private lastTouchTapSide: 'backward' | 'forward' | null = null;
  private touchSeekPointers = new Map<number, {
    startX: number;
    startY: number;
    startedAt: number;
    moved: boolean;
    eligible: boolean;
  }>();

  // --- Computed Real Time ---
  currentRealTime = computed(() => {
    return this.getRealTimeFromSeconds(this.currentTimeSec());
  });

  // Recording end time = start + total duration
  endTime = computed(() => {
    return this.getRealTimeFromSeconds(this.totalDuration());
  });

  // --- Diagnostics ---
  showDiagnostics = signal(false);
  diagCopied = signal(false);
  diagCopyFailed = signal(false);
  diagText = signal('');
  private lastPsvError = signal<string | null>(null);
  private lastMediaErrorText = signal<string | null>(null);

  /**
   * PSV does not always report a failure: an unreachable source can leave it
   * on "Loading…" forever without ever emitting panorama-error, which was the
   * exact case that left affected users with no way to reach diagnostics. Treat
   * "still not loaded after this long" as a failure for trigger purposes.
   */
  private readonly LOAD_TIMEOUT_MS = 15000;
  private loadTimedOut = signal(false);
  private loadTimeout: any = null;

  /**
   * The diagnostics trigger only exists once something has actually gone
   * wrong — it is a support affordance, not a normal player control, and
   * should not sit over healthy playback.
   */
  hasError = computed(
    () =>
      this.lastPsvError() !== null ||
      this.lastMediaErrorText() !== null ||
      this.loadTimedOut()
  );
  private staticDiag: string[] | null = null;
  private watchedVideo: HTMLVideoElement | null = null;
  private failedMedia: HTMLMediaElement | null = null;
  private mediaErrorCapture: ((e: Event) => void) | null = null;
  private sourceProbe = signal<string>('not checked');
  private autoplayProbe = signal<string>('not checked');
  private diagCopyTimeout: any = null;
  private diagRefreshInterval: any = null;
  private readonly diagInitMs = Date.now();
  // Sampled over time by the refresh tick: a single snapshot cannot tell
  // "never decoded a frame" from "caught it before the first frame".
  private maxDecodedFrames = 0;
  private maxCurrentTime = 0;
  private timeAdvanced = false;
  private contextLost = false;
  private psvReady = false;
  private psvLoaded = false;

  private viewer: Viewer | null = null;
  private videoPlugin: any;
  private seedApplied = false;
  private destroyRef = inject(DestroyRef);
  private userActivityTimeout: any;

  constructor() {
    afterNextRender(() => {
      this.installMediaErrorCapture();
      this.startLoadWatchdog();
      this.initViewer();
      this.initCanvas(); 
      this.trackUserActivity();
      this.listenToFullscreenChange();
    });

    this.destroyRef.onDestroy(() => {
      this.stopRewind();
      clearTimeout(this.arrowHoldTimeout);
      this.abortRecording();
      this.viewer?.destroy();
      this.teardownPreviewViewer();
      clearTimeout(this.userActivityTimeout);
      clearTimeout(this.seekFeedbackTimeout);
      clearTimeout(this.touchTapResetTimeout);
      clearTimeout(this.diagCopyTimeout);
      clearTimeout(this.loadTimeout);
      this.stopDiagRefresh();
      if (this.mediaErrorCapture) {
        document.removeEventListener('error', this.mediaErrorCapture, true);
        this.mediaErrorCapture = null;
      }
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    });

    effect(() => {
      const url = this.videoUrl();
      const data = this.panoData();

      // Read recording state non-reactively: toggling isRecording must NOT cause this
      // effect (which reloads the panorama and resets PTZ) to re-fire.
      if (untracked(() => this.isRecording())) {
        // Angle/source switched mid-recording — finalize and download before re-seeding the viewer.
        this.stopRecording();
      }

      if (this.viewer) {
        const currentTime = this.videoPlugin?.video ? this.videoPlugin.video.currentTime : 0;
        const wasPlaying = !this.videoPlugin?.video?.paused;
        this.seedApplied = false;
        // Errors recorded for the previous source must not survive into the
        // diagnostics of a different one.
        this.clearRecordedErrors();

        this.viewer.setPanorama({ source: url }, {
          panoData: data,
          transition: false,
          position: {
            pitch: this.DEFAULT_PITCH,
            yaw: 0,
          }
        }).then(() => {
          if (this.videoPlugin?.video) {
            this.videoPlugin.video.currentTime = currentTime;
            if (wasPlaying) {
              this.videoPlugin.video.play().catch(() => {});
            }
          }
          this.seedApplied = true;
          // The adapter builds a fresh <video> element per setPanorama, so the
          // previous error listener is now attached to a detached element.
          this.watchMediaErrors();
          this.refreshDiagnosticsIfOpen();
        }).catch((err: any) => {
          this.recordPsvError(err);
        });
      }
    });
  }

  // =========================================================
  // CANVAS & DRAWING LOGIC (Render Loop)
  // =========================================================

  private initCanvas() {
    const canvas = this.canvasRef().nativeElement;
    this.ctx = canvas.getContext('2d');
    this.resizeCanvas();
    // Observe the canvas itself, not the wrapper: in recording mode the wrapper
    // stays full-size while the canvas shrinks to the letterboxed aspect, so
    // observing the wrapper would miss the size change and leave the drawing
    // buffer misaligned with its CSS box.
    const resizeObserver = new ResizeObserver(() => this.resizeCanvas());
    resizeObserver.observe(canvas);
    this.renderLoop();
  }

  private resizeCanvas() {
    const canvas = this.canvasRef().nativeElement;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    if (cssWidth === 0 || cssHeight === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    if (this.ctx) {
      this.ctx.setTransform(1, 0, 0, 1, 0, 0); // reset prior scale before re-scaling
      this.ctx.scale(dpr, dpr);
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.lineWidth = 8;
    }
  }

  toggleDrawMode() {
    this.isDrawMode.update(v => !v);
  }

  clearDrawings() {
    this.strokes = [];
    this.currentStroke = null;
    this.hasDrawings.set(false);
  }

  startDrawing(event: MouseEvent | TouchEvent) {
    if (!this.isDrawMode()) return;
    this.isDrawing = true;
    const pos = this.getEventPos(event);
    this.currentStroke = { points: [pos], completedAt: null };
    this.strokes.push(this.currentStroke);
    this.hasDrawings.set(true);
  }

  draw(event: MouseEvent | TouchEvent) {
    if (!this.isDrawing || !this.isDrawMode() || !this.currentStroke) return;
    event.preventDefault(); 
    const pos = this.getEventPos(event);
    this.currentStroke.points.push(pos);
  }

  stopDrawing() {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    if (this.currentStroke) {
      this.currentStroke.completedAt = Date.now();
      this.currentStroke = null;
    }
  }

  private getEventPos(event: MouseEvent | TouchEvent): Point {
    const canvas = this.canvasRef().nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    if (window.TouchEvent && event instanceof TouchEvent) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = (event as MouseEvent).clientX;
      clientY = (event as MouseEvent).clientY;
    }
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  private renderLoop() {
    this.updateAndDraw();
    this.rafId = requestAnimationFrame(() => this.renderLoop());
  }

  private updateAndDraw() {
    if (!this.ctx) return;
    const canvas = this.canvasRef().nativeElement;
    const width = canvas.width / (window.devicePixelRatio || 1);
    const height = canvas.height / (window.devicePixelRatio || 1);
    const now = Date.now();

    this.ctx.clearRect(0, 0, width, height);

    this.strokes = this.strokes.filter(stroke => {
      if (!stroke.completedAt) return true;
      const age = now - stroke.completedAt;
      return age < (this.FADE_DELAY_MS + this.FADE_DURATION_MS);
    });

    this.hasDrawings.set(this.strokes.length > 0);

    this.strokes.forEach(stroke => {
      if (!this.ctx) return;
      let alpha = 1;
      if (stroke.completedAt) {
        const age = now - stroke.completedAt;
        if (age > this.FADE_DELAY_MS) {
          const progress = (age - this.FADE_DELAY_MS) / this.FADE_DURATION_MS;
          alpha = Math.max(0, 1 - progress);
        }
      }
      this.ctx.beginPath();
      this.ctx.strokeStyle = `rgba(250, 204, 21, ${alpha})`;
      if (stroke.points.length > 0) {
        this.ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
        for (let i = 1; i < stroke.points.length; i++) {
          this.ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }
      }
      this.ctx.stroke();
    });
  }

  // =========================================================
  // HOST LISTENERS
  // =========================================================

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      if (!this.isDrawMode()) {
        this.isDrawMode.set(true);
      }
      return; 
    }

    if (!this.videoPlugin?.video) return;

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      if (this.isArrowKeyInputTarget(event.target)) return;
      event.preventDefault();
      if (event.repeat || this.playbackModifier !== 'none') return;
      this.scheduleArrowHold(event.key);
      return;
    }

    if (this.isKeyboardInputTarget(event.target)) return;
    if (event.repeat) return;
    if (this.playbackModifier !== 'none') return;
    
    switch (event.key) {
      case 'ArrowDown': this.startMomentaryAction('slow', 0.5); break;
      case ' ': this.togglePlay(); break;
    }
  }

  @HostListener('window:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      this.isDrawMode.set(false);
      this.stopDrawing();
      return;
    }

    if (!this.videoPlugin?.video) return;
    const isPendingArrowTap = event.key === this.heldArrowKey && this.playbackModifier === 'none';
    if (event.key === this.heldArrowKey) {
      clearTimeout(this.arrowHoldTimeout);
      this.heldArrowKey = null;
    }
    if (isPendingArrowTap && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      this.seekBy(event.key === 'ArrowRight' ? 5 : -5, 5, true);
      return;
    }
    switch (event.key) {
      case 'ArrowRight': if (this.playbackModifier === 'fast') this.endHeldFastForward(); break;
      case 'ArrowLeft': if (this.playbackModifier === 'reverse') this.endHeldRewind(); break;
      case 'ArrowDown': if (this.playbackModifier === 'slow') this.endMomentaryAction(); break;
    }
  }

  private isKeyboardInputTarget(target: EventTarget | null): boolean {
    return target instanceof HTMLElement &&
      (target.isContentEditable || target.matches('input, textarea, select, button'));
  }

  private isArrowKeyInputTarget(target: EventTarget | null): boolean {
    return target instanceof HTMLElement &&
      (target.isContentEditable || target.matches('input, textarea, select'));
  }

  private scheduleArrowHold(key: 'ArrowLeft' | 'ArrowRight') {
    clearTimeout(this.arrowHoldTimeout);
    this.heldArrowKey = key;
    this.arrowHoldTimeout = setTimeout(() => {
      if (this.heldArrowKey !== key || this.playbackModifier !== 'none') return;
      if (key === 'ArrowRight') this.startHeldFastForward();
      else this.startHeldRewind();
    }, 300);
  }

  private seekBy(deltaSeconds: number, feedbackSeconds: number, accumulateFeedback = false) {
    const video: HTMLVideoElement | undefined = this.videoPlugin?.video;
    if (!video) return;
    const duration = Number.isFinite(video.duration) ? video.duration : this.totalDuration();
    const nextTime = Math.max(0, Math.min(duration > 0 ? duration : Infinity, video.currentTime + deltaSeconds));
    this.videoPlugin.setTime(nextTime);
    this.currentTimeSec.set(nextTime);
    this.progressPercentage.set(duration > 0 ? (nextTime / duration) * 100 : 0);
    this.showSeekFeedback(deltaSeconds < 0 ? 'backward' : 'forward', feedbackSeconds, accumulateFeedback);
  }

  private showSeekFeedback(direction: 'backward' | 'forward', seconds: number, accumulate: boolean) {
    const displayedSeconds = accumulate && this.seekFeedbackDirection() === direction
      ? this.seekFeedbackSeconds() + seconds
      : seconds;
    this.seekFeedbackDirection.set(direction);
    this.seekFeedbackSeconds.set(displayedSeconds);
    clearTimeout(this.seekFeedbackTimeout);
    this.seekFeedbackTimeout = setTimeout(() => {
      this.seekFeedbackDirection.set(null);
      this.seekFeedbackSeconds.set(0);
    }, 1600);
  }

  onSeekPointerDown(event: PointerEvent) {
    if (event.pointerType !== 'touch') return;
    const target = event.target as HTMLElement | null;
    const eligible = !this.isDrawMode() &&
      !target?.closest('.controls-overlay, .diag-wrapper');
    this.touchSeekPointers.set(event.pointerId, {
      startX: event.clientX,
      startY: event.clientY,
      startedAt: Date.now(),
      moved: false,
      eligible,
    });
    if (this.touchSeekPointers.size > 1) {
      this.touchSeekPointers.forEach(pointer => pointer.eligible = false);
    }
  }

  onSeekPointerMove(event: PointerEvent) {
    const pointer = this.touchSeekPointers.get(event.pointerId);
    if (!pointer) return;
    if (Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY) > 18) {
      pointer.moved = true;
    }
  }

  onSeekPointerUp(event: PointerEvent) {
    const pointer = this.touchSeekPointers.get(event.pointerId);
    this.touchSeekPointers.delete(event.pointerId);
    if (!pointer?.eligible || pointer.moved || Date.now() - pointer.startedAt > 350) return;

    const rect = this.playerWrapperRef().nativeElement.getBoundingClientRect();
    const relativeX = (event.clientX - rect.left) / rect.width;
    const side = relativeX < 0.4 ? 'backward' : relativeX > 0.6 ? 'forward' : null;
    if (!side) {
      this.resetTouchTapSequence();
      return;
    }

    const now = Date.now();
    const maxGap = this.touchTapCount >= 2 ? 650 : 450;
    if (side === this.lastTouchTapSide && now - this.lastTouchTapAt <= maxGap) {
      this.touchTapCount += 1;
    } else {
      this.touchTapCount = 1;
      this.lastTouchTapSide = side;
    }
    this.lastTouchTapAt = now;

    clearTimeout(this.touchTapResetTimeout);
    this.touchTapResetTimeout = setTimeout(() => this.resetTouchTapSequence(), this.touchTapCount >= 2 ? 650 : 450);

    if (this.touchTapCount >= 2) {
      event.preventDefault();
      this.seekBy(side === 'backward' ? -10 : 10, (this.touchTapCount - 1) * 10);
    }
  }

  onSeekPointerCancel(event: PointerEvent) {
    this.touchSeekPointers.delete(event.pointerId);
  }

  private resetTouchTapSequence() {
    clearTimeout(this.touchTapResetTimeout);
    this.touchTapCount = 0;
    this.lastTouchTapAt = 0;
    this.lastTouchTapSide = null;
  }

  // =========================================================
  // VIDEO & VIEWER LOGIC
  // =========================================================

  private getRealTimeFromSeconds(seconds: number): Date {
    const startInput = this.recordingStartTime();
    const timeTracker = new Date(startInput);
    if (!isNaN(timeTracker.getTime())) {
      timeTracker.setSeconds(timeTracker.getSeconds() + seconds);
      return timeTracker;
    }
    return new Date();
  }

  private initViewer() {
    const container = this.viewerContainerRef().nativeElement;
    this.viewer = new Viewer({
      container,
      minFov: 10,
      defaultPitch: this.DEFAULT_PITCH,
      adapter: EquirectangularVideoAdapter.withConfig({
        muted: true,
        autoplay: true,
        resolution: 128, 
      }),
      navbar: false, 
      panorama: { source: this.videoUrl() },
      panoData: this.panoData(),
      plugins: [[VideoPlugin, { progressbar: false, bigbutton: false }]],
    });

    this.videoPlugin = this.viewer.getPlugin(VideoPlugin);
    this.videoPlugin.addEventListener('play-pause', (e: any) => this.isPlaying.set(e.playing));
    const seed = this.startTimeSec();
    let seedAttempted = false;
    this.videoPlugin.addEventListener('progress', (e: any) => {
      if (!seedAttempted) {
        const v = this.videoPlugin?.video;
        const dur = v?.duration || e.duration || 0;
        if (v && dur > 0) {
          seedAttempted = true;
          if (seed > 0 && seed < dur) {
            v.currentTime = seed;
          }
          this.seedApplied = true;
        }
      }
      if (this.seedApplied) this.timeUpdate.emit(e.time);
      if (!this.isScrubbing()) {
        this.currentTimeSec.set(e.time);
        this.totalDuration.set(e.duration);
        this.progressPercentage.set(e.progress * 100);
      }
    });
    this.videoPlugin.addEventListener('volume-change', (e: any) => {
      this.volume.set(e.volume);
      this.isMuted.set(e.volume === 0);
    });
    // Capture the failure behind PSV's generic "The panorama cannot be loaded"
    // overlay so the diagnostics panel can report something actionable.
    this.viewer.addEventListener('panorama-error', (e: any) => {
      this.recordPsvError(e?.error ?? e);
    });
    this.viewer.addEventListener('panorama-loaded', () => {
      this.psvLoaded = true;
      // Loaded in time — disarm the watchdog so it can never fire late.
      clearTimeout(this.loadTimeout);
      this.refreshDiagnosticsIfOpen();
    });
    // A lost WebGL context produces exactly this failure shape: media decodes
    // normally, nothing renders, and no media or PSV error is raised. iOS
    // drops contexts under memory pressure far more readily than iPadOS.
    const canvas = container.querySelector('canvas');
    canvas?.addEventListener('webglcontextlost', () => {
      this.contextLost = true;
      console.error('[pano] WebGL context lost');
      this.refreshDiagnosticsIfOpen();
    });
    this.viewer.addEventListener('ready', () => {
      this.psvReady = true;
      const v = this.videoPlugin?.video;
      if (v) {
        this.totalDuration.set(v.duration || 0);
        this.volume.set(v.volume);
        this.isMuted.set(v.muted);
      }
      this.watchMediaErrors();
    }, { once: true });
  }

  // =========================================================
  // DIAGNOSTICS
  // =========================================================

  private clearRecordedErrors() {
    this.lastPsvError.set(null);
    this.lastMediaErrorText.set(null);
    this.failedMedia = null;
    this.startLoadWatchdog();
    // hasError() now gates the whole trigger+panel, so clearing the errors
    // unmounts them. Close explicitly, or showDiagnostics stays true with no
    // panel to close it — leaving the refresh interval running and causing the
    // panel to reappear by itself, with stale probe data, on the next error.
    if (this.showDiagnostics()) this.closeDiagnostics();
  }

  /**
   * (Re)arms the load watchdog. Called at startup and whenever the source
   * changes, so each panorama gets its own grace period.
   */
  private startLoadWatchdog() {
    clearTimeout(this.loadTimeout);
    this.loadTimedOut.set(false);
    this.psvLoaded = false;
    this.loadTimeout = setTimeout(() => {
      if (!this.psvLoaded) this.loadTimedOut.set(true);
    }, this.LOAD_TIMEOUT_MS);
  }

  /**
   * Reads the MediaError before anyone else can clear it.
   *
   * PSV resets the failing <video> as part of its own error handling, so by
   * the time the 'panorama-error' listener runs, element.error is already
   * null — the panel reported "mediaError: none" for a failure that plainly
   * had one. Media 'error' events don't bubble, but they do capture, so a
   * document-level capture listener sees them first.
   */
  private installMediaErrorCapture() {
    this.mediaErrorCapture = (e: Event) => {
      const t: any = e.target;
      if (t?.tagName !== 'VIDEO' && t?.tagName !== 'AUDIO') return;
      this.failedMedia = t as HTMLMediaElement;
      this.captureMediaError(t as HTMLMediaElement);
      this.refreshDiagnosticsIfOpen();
    };
    document.addEventListener('error', this.mediaErrorCapture, true);
  }

  private recordPsvError(err: any) {
    // PSV reports media failures as a DOM 'error' Event, not an Error — the
    // useful payload (the MediaError, and which element failed) hangs off
    // event.target, so unwrap that before falling back to string coercion.
    let text: string;
    const target: any = err?.target;
    // Only a media element carries a MediaError — a canvas/img target would
    // otherwise produce "canvas load failed (see mediaError)" next to
    // "mediaError: none".
    if (target?.tagName === 'VIDEO' || target?.tagName === 'AUDIO') {
      this.captureMediaError(target as HTMLMediaElement);
      text = `${target.tagName.toLowerCase()} load failed (see mediaError)`;
    } else {
      text =
        err?.message ||
        (typeof err === 'string' ? err : '') ||
        err?.name ||
        (err ? `${err.constructor?.name ?? typeof err}: ${String(err)}` : '') ||
        'unknown panorama load failure';
    }
    // A failed setPanorama surfaces both as a 'panorama-error' event and as a
    // rejected promise — record the first, ignore the duplicate.
    if (this.lastPsvError() === text) return;
    this.lastPsvError.set(text);
    // Keep it in the browser console too, for cases where the user can be
    // walked through remote inspection.
    console.error('[pano] panorama load error:', err);
    // Only fall back to the plugin's element if the event didn't already
    // identify the failing one — the plugin may hold a different (older)
    // element whose stale error would overwrite the accurate capture above.
    if (!(target?.tagName === 'VIDEO' || target?.tagName === 'AUDIO')) {
      this.captureMediaError();
    }
    this.refreshDiagnosticsIfOpen();
  }

  /**
   * Byte-range probe of the actual video URL. Distinguishes "Safari refused the
   * file" from "the file never arrived": reports the HTTP status, the
   * Content-Type the bucket is serving (an object stored as
   * application/octet-stream will not decode in Safari), and whether range
   * requests are honoured (Safari needs them to start playback).
   */
  private async probeSource() {
    this.sourceProbe.set('checking...');
    // A server that ignores the Range header answers 200 with the WHOLE file.
    // fetch() resolves on headers, so without an explicit abort the rest of a
    // multi-hundred-MB video keeps streaming in the background — on the very
    // cellular connections this probe is meant to diagnose.
    const abort = new AbortController();
    try {
      const res = await fetch(this.videoUrl(), {
        method: 'GET',
        headers: { Range: 'bytes=0-0' },
        signal: abort.signal,
      });
      const parts = [
        `status=${res.status}`,
        `type=${res.headers.get('content-type') ?? 'none'}`,
        `ranges=${res.headers.get('accept-ranges') ?? 'none'}`,
        `len=${res.headers.get('content-range') ?? res.headers.get('content-length') ?? 'none'}`,
      ];
      this.sourceProbe.set(parts.join(' '));
    } catch (err: any) {
      this.sourceProbe.set(`fetch failed: ${err?.name ?? 'Error'} ${err?.message ?? ''}`.trim());
    } finally {
      // Headers are all we wanted; drop the body regardless of outcome.
      abort.abort();
    }
    this.refreshDiagnosticsIfOpen();
  }

  /**
   * Distinguishes an autoplay block (Low Power Mode, or no user gesture yet)
   * from a decode failure — without synthesising a media file, since a stub
   * source that never becomes playable leaves play() pending forever rather
   * than rejecting.
   *
   * AudioContext state is the side-effect-free proxy: browsers that block
   * autoplay start it 'suspended' and only resume it after a user gesture.
   * iOS Low Power Mode suppresses both audio and muted-video autoplay, so a
   * suspended context on a page the user has already tapped is a strong
   * signal. Reported alongside the real element's observed state rather than
   * as a verdict on its own.
   */
  private probeAutoplay() {
    const parts: string[] = [];

    try {
      const Ctor: typeof AudioContext =
        (window as any).AudioContext ?? (window as any).webkitAudioContext;
      if (Ctor) {
        const ctx = new Ctor();
        parts.push(`audioContext=${ctx.state}`);
        void ctx.close();
      } else {
        parts.push('audioContext=unavailable');
      }
    } catch {
      parts.push('audioContext=error');
    }

    // Whether the real panorama video ever actually started is the ground
    // truth; the probe above only explains *why* it might not have.
    const v: HTMLVideoElement | undefined = this.videoPlugin?.video;
    if (!v) {
      parts.push('media=no element');
    } else if (v.played.length > 0) {
      parts.push('media=started');
    } else {
      parts.push(`media=never started (paused=${v.paused})`);
    }

    try {
      parts.push(`userActivated=${(navigator as any).userActivation?.hasBeenActive ?? 'unknown'}`);
    } catch {
      /* non-standard on older Safari */
    }

    this.autoplayProbe.set(parts.join(' '));
    this.refreshDiagnosticsIfOpen();
  }

  private refreshDiagnosticsIfOpen() {
    if (this.showDiagnostics()) {
      this.diagText.set(this.buildDiagnosticsText());
    }
  }

  /**
   * The <video> element PSV creates is not exposed until the adapter has built
   * it, so attach the error listener once the viewer is ready. Codec/decode
   * failures on iOS surface here (MediaError code 3 = decode, 4 = src not
   * supported) rather than as a JS exception.
   */
  private watchMediaErrors() {
    const v: HTMLVideoElement | undefined = this.videoPlugin?.video;
    if (!v || v === this.watchedVideo) return;
    this.watchedVideo = v;
    v.addEventListener('error', () => {
      this.captureMediaError();
      this.refreshDiagnosticsIfOpen();
    });
    this.captureMediaError();
  }

  /**
   * @param element the failing media element when known. On a load failure PSV
   * never publishes the video on the plugin, so the element from the error
   * event is the only way to reach the MediaError.
   */
  private captureMediaError(element?: HTMLMediaElement) {
    const live: HTMLMediaElement | undefined = this.videoPlugin?.video;
    const v: HTMLMediaElement | undefined = element ?? live;
    const err = v?.error;
    if (!err) {
      // "Refresh from current truth" only makes sense when there IS a live
      // element to read. With no element (the load-failure case) the earlier
      // capture-phase reading is the only record we have — never wipe it.
      if (!element && live) this.lastMediaErrorText.set(null);
      return;
    }
    const names: Record<number, string> = {
      1: 'MEDIA_ERR_ABORTED',
      2: 'MEDIA_ERR_NETWORK',
      3: 'MEDIA_ERR_DECODE',
      4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
    };
    this.lastMediaErrorText.set(
      `${err.code} ${names[err.code] ?? 'UNKNOWN'}${err.message ? ` — ${err.message}` : ''}`
    );
  }

  private lastDiagToggleMs = 0;
  private diagPointerId: number | null = null;

  onDiagPointerDown(event: PointerEvent) {
    event.stopPropagation();
    if (event.pointerType === 'mouse') return;
    event.preventDefault();
    this.diagPointerId = event.pointerId;
    try {
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    } catch {}
    // Toggle immediately. Android can cancel a pointer sequence while the
    // panorama is stalled, so waiting for pointerup makes the button inert in
    // exactly the failure state where it is needed.
    this.toggleDiagnostics();
  }

  /**
   * Pointer-up only releases capture; touch activation already happened on
   * pointer-down so a cancelled loading-surface gesture cannot swallow it.
   */
  onDiagPointerUp(event: PointerEvent) {
    event.stopPropagation();
    if (event.pointerType === 'mouse') return;
    const pointerId = this.diagPointerId;
    this.diagPointerId = null;
    if (pointerId !== event.pointerId) return;
    try {
      (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    } catch {}
    event.preventDefault();
  }

  onDiagPointerCancel(event: PointerEvent) {
    event.stopPropagation();
    if (this.diagPointerId === event.pointerId) this.diagPointerId = null;
  }

  /**
   * Mouse and keyboard activation fallback. Touch and pen activation happen on
   * pointerup above; the timestamp guard ignores any synthesized follow-up click.
   */
  onDiagTrigger(event: Event) {
    event.stopPropagation();
    this.toggleDiagnostics();
  }

  private toggleDiagnostics() {
    const now = Date.now();
    if (now - this.lastDiagToggleMs < 400) return;
    this.lastDiagToggleMs = now;

    const next = !this.showDiagnostics();
    this.showDiagnostics.set(next);
    if (next) this.primeDiagnostics();
    else this.stopDiagRefresh();
  }

  private primeDiagnostics() {
    this.diagCopied.set(false);
    this.diagCopyFailed.set(false);
    this.diagText.set(this.buildDiagnosticsText());
    // probeSource is async and refreshes the panel when it lands.
    void this.probeSource();
    this.probeAutoplay();
    this.startDiagRefresh();
  }

  /**
   * A one-shot snapshot cannot distinguish "permanently stuck" from "still
   * loading" — readyState/networkState/buffered look identical a second after
   * load and a minute into a hang. Re-read them on a timer so the elapsed
   * field gives whoever reads the report that context.
   *
   * Only the live element fields are recomputed; probeSource is not re-run.
   */
  private startDiagRefresh() {
    this.stopDiagRefresh();
    this.diagRefreshInterval = setInterval(() => {
      this.sampleProgress();
      if (!this.showDiagnostics()) return;
      this.diagText.set(this.buildDiagnosticsText());
    }, 1000);
  }

  /**
   * Frame count is the field that separates "media is fine, rendering is
   * broken" from "media never decoded" — the distinction a readyState/buffered
   * snapshot cannot make, since iOS reports empty buffered ranges even during
   * healthy playback.
   */
  private decodedFrames(v?: HTMLVideoElement | null): number {
    if (!v) return 0;
    const anyV = v as any;
    if (typeof anyV.webkitDecodedFrameCount === 'number') return anyV.webkitDecodedFrameCount;
    if (typeof anyV.getVideoPlaybackQuality === 'function') {
      return anyV.getVideoPlaybackQuality()?.totalVideoFrames ?? 0;
    }
    return 0;
  }

  /**
   * Definitive taint test. If the video was fetched without a valid CORS
   * response, drawing it to a canvas taints that canvas and getImageData
   * throws SecurityError — the same condition that makes WebGL silently
   * refuse to texture the frame, producing "plays fine, renders nothing"
   * with no error anywhere.
   */
  private probeTaint(v?: HTMLVideoElement | null): string {
    if (!v) return 'no video element';
    if (v.readyState < 2) return 'unknown (no frame yet)';
    try {
      const c = document.createElement('canvas');
      c.width = 2;
      c.height = 2;
      const ctx = c.getContext('2d');
      if (!ctx) return 'unknown (no 2d context)';
      ctx.drawImage(v, 0, 0, 2, 2);
      ctx.getImageData(0, 0, 1, 1);
      return 'no';
    } catch (err: any) {
      return `YES (${err?.name ?? 'Error'})`;
    }
  }

  private sampleProgress() {
    const v: HTMLVideoElement | undefined =
      this.videoPlugin?.video ?? (this.failedMedia as HTMLVideoElement | null) ?? undefined;
    if (!v) return;
    this.maxDecodedFrames = Math.max(this.maxDecodedFrames, this.decodedFrames(v));
    if (v.currentTime > this.maxCurrentTime + 0.01) this.timeAdvanced = true;
    this.maxCurrentTime = Math.max(this.maxCurrentTime, v.currentTime);
  }

  private stopDiagRefresh() {
    if (this.diagRefreshInterval) {
      clearInterval(this.diagRefreshInterval);
      this.diagRefreshInterval = null;
    }
  }

  closeDiagnostics() {
    this.showDiagnostics.set(false);
    this.stopDiagRefresh();
  }

  /**
   * Static, device-level facts. Probed once and cached: creating a throwaway
   * WebGL context is not free, and none of these change for the page lifetime.
   */
  private getStaticDiagnostics(): string[] {
    if (this.staticDiag) return this.staticDiag;
    const lines: string[] = [];

    let renderer = 'unavailable';
    let maxTexture = 'unavailable';
    let glVersion = 'none';
    try {
      const probe = document.createElement('canvas');
      const gl = (probe.getContext('webgl2') ??
        probe.getContext('webgl') ??
        probe.getContext('experimental-webgl')) as WebGLRenderingContext | null;
      if (gl) {
        glVersion = gl.getParameter(gl.VERSION) || 'unknown';
        maxTexture = String(gl.getParameter(gl.MAX_TEXTURE_SIZE));
        const ext = gl.getExtension('WEBGL_debug_renderer_info');
        if (ext) renderer = gl.getParameter((ext as any).UNMASKED_RENDERER_WEBGL) || 'masked';
      }
    } catch {
      /* probe must never break the panel */
    }

    lines.push(`webgl: ${glVersion}`);
    lines.push(`gpu: ${renderer}`);
    lines.push(`maxTextureSize: ${maxTexture}`);

    try {
      const probe = document.createElement('video');
      const types = [
        'video/mp4; codecs="avc1.640028"',
        'video/mp4; codecs="hvc1.1.6.L120.90"',
        'video/webm; codecs="vp9"',
      ];
      lines.push(...types.map((t) => `canPlayType ${t}: ${probe.canPlayType(t) || 'no'}`));
    } catch {
      lines.push('canPlayType: unavailable');
    }

    lines.push(`screen: ${window.screen?.width}x${window.screen?.height} @${window.devicePixelRatio || 1}x`);
    lines.push(`userAgent: ${navigator.userAgent}`);
    this.staticDiag = lines;
    return lines;
  }

  private buildDiagnosticsText(): string {
    // On a load failure PSV never publishes the video on the plugin, so fall
    // back to whichever element actually failed — otherwise every state field
    // reads "n/a" for precisely the case we are trying to debug.
    const v: HTMLVideoElement | undefined =
      this.videoPlugin?.video ?? (this.failedMedia as HTMLVideoElement | null) ?? undefined;
    this.captureMediaError();

    // Only the host — the source URL is signed and must not end up in a
    // screenshot pasted into a support thread.
    let host = 'unknown';
    try {
      host = new URL(this.videoUrl(), window.location.href).host;
    } catch {
      /* leave as unknown */
    }

    const pano = this.panoData() as any;
    const elapsedSec = Math.round((Date.now() - this.diagInitMs) / 1000);
    // Metadata parsed (readyState >= 1) but nothing buffered after a grace
    // period means the decoder is accepting the container and producing no
    // frames — typically a stream the hardware decoder cannot handle.
    const stalled =
      !!v && v.readyState >= 1 && v.buffered.length === 0 && elapsedSec >= 10
        ? ` STALLED ${elapsedSec}s`
        : '';

    const lines = [
      `time: ${new Date().toISOString()}`,
      `elapsed: ${elapsedSec}s since player init${stalled}`,
      `panoramaError: ${this.lastPsvError() ?? 'none'}`,
      `mediaError: ${this.lastMediaErrorText() ?? 'none'}`,
      `readyState: ${v ? v.readyState : 'no video element'}`,
      `networkState: ${v ? v.networkState : 'n/a'}`,
      `videoSize: ${v ? `${v.videoWidth}x${v.videoHeight}` : 'n/a'}`,
      `duration: ${v?.duration ?? 'n/a'}`,
      `paused: ${v ? v.paused : 'n/a'}`,
      `buffered: ${v && v.buffered.length ? `${v.buffered.start(0)}-${v.buffered.end(v.buffered.length - 1)}` : 'nothing'}`,
      `currentTime: ${v?.currentTime ?? 'n/a'} advanced=${this.timeAdvanced}`,
      `decodedFrames: ${this.decodedFrames(v)} max=${this.maxDecodedFrames}`,
      `psv: ready=${this.psvReady} loaded=${this.psvLoaded} contextLost=${this.contextLost}`,
      `tainted: ${this.probeTaint(v)}`,
      `autoplay: ${this.autoplayProbe()}`,
      `sourceProbe: ${this.sourceProbe()}`,
      `sourceHost: ${host}`,
      `panoData: ${pano?.fullWidth ?? '?'}x${pano?.fullHeight ?? '?'} croppedW=${pano?.croppedWidth ?? '?'} croppedH=${pano?.croppedHeight ?? '?'}`,
      ...this.getStaticDiagnostics(),
    ];
    return lines.join('\n');
  }

  async copyDiagnostics() {
    // Always rebuild: video state may have changed since the panel was opened.
    const text = this.buildDiagnosticsText();
    this.diagText.set(text);
    clearTimeout(this.diagCopyTimeout);
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      // Older iOS Safari and most in-app browsers block the async clipboard API.
      ok = this.legacyCopy(text);
    }
    this.diagCopied.set(ok);
    this.diagCopyFailed.set(!ok);
    if (ok) {
      this.diagCopyTimeout = setTimeout(() => this.diagCopied.set(false), 2000);
    }
  }

  private legacyCopy(text: string): boolean {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  private startMomentaryAction(modifier: 'slow', rate: number) {
    this.playbackModifier = modifier;
    this.actionStartTime = Date.now();
    this.wasPlayingBeforeAction = !this.videoPlugin.video.paused;
    this.videoPlugin.video.playbackRate = rate;
    this.videoPlugin.video.play().catch(() => {});
  }

  private endMomentaryAction() {
    const duration = Date.now() - this.actionStartTime;
    this.videoPlugin.video.playbackRate = 1.0;
    this.playbackModifier = 'none';
    if (duration < this.TAP_THRESHOLD_MS) {
      if (this.wasPlayingBeforeAction) this.videoPlugin.pause(); else this.videoPlugin.play();
    } else {
      if (!this.wasPlayingBeforeAction) this.videoPlugin.pause();
    }
  }

  private startHeldFastForward() {
    const video = this.videoPlugin.video;
    this.playbackModifier = 'fast';
    this.wasPlayingBeforeAction = !video.paused;
    video.playbackRate = 2;
    video.play().catch(() => {});
  }

  private endHeldFastForward() {
    const video = this.videoPlugin.video;
    video.playbackRate = 1;
    this.playbackModifier = 'none';
    if (!this.wasPlayingBeforeAction) this.videoPlugin.pause();
  }

  private startHeldRewind() {
    const video = this.videoPlugin.video;
    this.playbackModifier = 'reverse';
    this.wasPlayingBeforeRewind = !video.paused;
    video.pause();
    this.rewindInterval = setInterval(() => {
      const newTime = Math.max(0, video.currentTime - 0.04);
      video.currentTime = newTime;
      this.currentTimeSec.set(newTime);
      this.progressPercentage.set(this.totalDuration() > 0 ? (newTime / this.totalDuration()) * 100 : 0);
      if (newTime <= 0) this.stopRewind();
    }, 40);
  }

  private stopRewind() {
    if (this.rewindInterval) {
      clearInterval(this.rewindInterval);
      this.rewindInterval = null;
    }
  }

  private endHeldRewind() {
    this.stopRewind();
    this.playbackModifier = 'none';
    if (this.wasPlayingBeforeRewind && this.videoPlugin?.video) {
      this.videoPlugin.video.play().catch(() => {});
    }
  }

  onScrubStart() { this.isScrubbing.set(true); }

  onScrubEnd() {
    this.isScrubbing.set(false);
    this.seekBarRef().nativeElement.blur();
  }

  private customScrubbing = false;

  onSliderAreaDown(event: PointerEvent) {
    if (event.target === this.seekBarRef().nativeElement) return; // native range handles its own drag
    if (event.button !== 0) return;
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    this.customScrubbing = true;
    this.isScrubbing.set(true);
    this.seekToClientX(event.clientX);
    this.updateTooltip(event.clientX);
  }

  onSliderAreaMove(event: PointerEvent) {
    this.updateTooltip(event.clientX);
    if (this.customScrubbing) {
      this.seekToClientX(event.clientX);
    }
  }

  onSliderAreaUp(event: PointerEvent) {
    if (!this.customScrubbing) return;
    const target = event.currentTarget as HTMLElement;
    if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId);
    this.customScrubbing = false;
    this.isScrubbing.set(false);
    this.seekBarRef().nativeElement.blur();
  }

  onSliderAreaLeave(_event: PointerEvent) {
    if (!this.customScrubbing && !this.isScrubbing()) this.showPreview.set(false);
  }

  private seekToClientX(clientX: number) {
    const range = this.seekBarRef().nativeElement;
    const rect = range.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, offsetX / rect.width));
    const newTime = percent * this.totalDuration();
    if (this.videoPlugin) this.videoPlugin.setTime(newTime);
    this.progressPercentage.set(percent * 100);
  }
  
  onScrubberTouchStart(event: TouchEvent) {
    this.isScrubbing.set(true);
    if (event.touches.length > 0) this.updateTooltip(event.touches[0].clientX);
  }
  
  onScrubberTouch(event: TouchEvent) {
    if (event.touches.length > 0) this.updateTooltip(event.touches[0].clientX);
  }
  
  onScrubberTouchEnd() {
    this.isScrubbing.set(false);
    this.showPreview.set(false);
    this.seekBarRef().nativeElement.blur();
  }

  private updateTooltip(clientX: number) {
    const range = this.seekBarRef().nativeElement;
    const rect = range.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, offsetX / rect.width));
    const previewSec = percentage * this.totalDuration();
    this.previewTime.set(this.getRealTimeFromSeconds(previewSec));
    this.previewLeftPx.set(offsetX);
    this.showPreview.set(true);
    this.requestPreviewFrame(previewSec);
  }

  togglePreview() {
    const next = !this.previewEnabled();
    this.previewEnabled.set(next);
    this.savePreviewPref(next);
    if (!next) this.teardownPreviewViewer();
  }

  toggleSettings(event: Event) {
    event.stopPropagation();
    this.showSettings.update((v) => !v);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (this.showSettings() && !target?.closest('.settings-wrapper')) {
      this.showSettings.set(false);
    }
    if (this.showRecordMenu() && !target?.closest('.record-wrapper')) {
      this.showRecordMenu.set(false);
    }
    if (this.showDiagnostics() && !target?.closest('.diag-wrapper')) {
      this.showDiagnostics.set(false);
    }
  }

  private requestPreviewFrame(previewSec: number) {
    if (!this.previewEnabled()) return;
    this.pendingPreviewSec = previewSec;
    if (this.previewSeekTimeout) return;
    this.previewSeekTimeout = setTimeout(() => {
      this.previewSeekTimeout = null;
      const sec = this.pendingPreviewSec;
      this.pendingPreviewSec = null;
      if (sec === null) return;
      this.ensurePreviewViewer().then(() => {
        if (!this.previewViewer || !this.viewer) return;
        const pos = this.viewer.getPosition();
        const zoom = this.viewer.getZoomLevel();
        this.previewViewer.rotate({ yaw: pos.yaw, pitch: pos.pitch });
        this.previewViewer.zoom(zoom);
        if (this.previewVideoPlugin?.video) {
          this.previewVideoPlugin.video.currentTime = sec;
        }
      });
    }, 60);
  }

  private async ensurePreviewViewer() {
    if (this.previewViewer) return;
    const container = this.previewContainerRef()?.nativeElement;
    if (!container) return;
    this.previewViewer = new Viewer({
      container,
      minFov: 10,
      defaultPitch: this.DEFAULT_PITCH,
      adapter: EquirectangularVideoAdapter.withConfig({
        muted: true,
        autoplay: false,
        resolution: 64,
      }),
      navbar: false,
      panorama: { source: this.videoUrl() },
      panoData: this.panoData(),
      plugins: [[VideoPlugin, { progressbar: false, bigbutton: false }]],
    });
    this.previewVideoPlugin = this.previewViewer.getPlugin(VideoPlugin);
    await new Promise<void>((resolve) => {
      this.previewViewer!.addEventListener('ready', () => {
        if (this.previewVideoPlugin?.video) {
          this.previewVideoPlugin.video.muted = true;
          this.previewVideoPlugin.video.pause();
        }
        resolve();
      }, { once: true });
    });
  }

  private teardownPreviewViewer() {
    if (this.previewSeekTimeout) {
      clearTimeout(this.previewSeekTimeout);
      this.previewSeekTimeout = null;
    }
    this.pendingPreviewSec = null;
    if (this.previewViewer) {
      try { this.previewViewer.destroy(); } catch {}
      this.previewViewer = null;
      this.previewVideoPlugin = null;
    }
  }

  togglePlay() {
    if (!this.videoPlugin) return;
    if (this.isPlaying()) this.videoPlugin.pause(); else this.videoPlugin.play();
  }
  toggleMute() {
    if (!this.videoPlugin) return;
    if (this.isMuted()) {
      this.videoPlugin.setMute(false);
      if (this.volume() === 0) this.videoPlugin.setVolume(1);
    } else {
      this.videoPlugin.setMute(true);
      this.videoPlugin.setVolume(0);
    }
  }

  changeVolume(event: Event) {
    if (!this.videoPlugin) return;
    const input = event.target as HTMLInputElement;
    const val = Number(input.value);
    this.videoPlugin.setVolume(val);
    if (val > 0 && this.isMuted()) this.videoPlugin.setMute(false);
    
    // Release focus so keys don't change volume
    input.blur();
  }

  seek(event: Event) {
    if (!this.videoPlugin) return;
    const input = event.target as HTMLInputElement;
    const percent = Number(input.value);
    const newTime = (percent / 100) * this.totalDuration();
    this.videoPlugin.setTime(newTime);
  }

  toggleFullscreen() {
    const wrapper = this.playerWrapperRef().nativeElement;
    if (!document.fullscreenElement) wrapper.requestFullscreen().catch(err => console.error(err));
    else document.exitFullscreen();
  }

  // =========================================================
  // RECORDING
  // =========================================================

  onRecordButtonClick(event: Event) {
    event.stopPropagation();
    if (this.isRecording()) {
      void this.stopRecording();
      return;
    }
    this.showRecordMenu.update((v) => !v);
  }

  async startRecording(format: RecordingFormat) {
    // isRecording() only flips true at the END of this method, after several
    // awaits. A second call arriving in that window (a double-tap on a format
    // button) would slip past it and orphan the first MediaRecorder and its
    // interval, so the re-entrancy guard has to be a synchronous flag.
    if (this.isRecording() || this.recordingStarting) return;
    this.recordingStarting = true;
    try {
      await this.beginRecording(format);
    } finally {
      this.recordingStarting = false;
    }
  }

  private async beginRecording(format: RecordingFormat) {
    this.showRecordMenu.set(false);

    this.recordingFormat.set(format);
    // PSV detects container resizes via its own ResizeObserver and rebuilds
    // its WebGL canvas to match — but that takes ~5 animation frames after
    // the aspect-ratio CSS class lands. If we start the recorder before that
    // happens, we composite from a canvas still rendered at the old (wider)
    // viewport, which gets stretched into the target output dimensions and
    // produces a distorted frame. Wait until the WebGL canvas's CSS size
    // actually matches the container size before proceeding.
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    this.viewer?.needsUpdate();
    await this.awaitViewerCanvasSettle();

    const media = this.videoPlugin?.video ?? null;
    const recorder = new PanoRecorder({
      getSourceCanvas: () => this.viewerContainerRef().nativeElement.querySelector('canvas'),
      getOverlayCanvas: () => this.canvasRef().nativeElement,
      getMediaElement: () => media,
    });
    try {
      recorder.start(format, this.fps() ?? 30);
    } catch (err) {
      console.error('Failed to start panorama recording', err);
      this.recordingFormat.set(null);
      return;
    }
    this.recorder = recorder;
    this.recordingStartedAtMs = Date.now();
    this.recordingElapsedSec.set(0);
    this.recordingTickInterval = setInterval(() => {
      this.recordingElapsedSec.set(Math.floor((Date.now() - this.recordingStartedAtMs) / 1000));
    }, 250);

    // Single continuous driver runs through both play and pause states. When
    // the underlying video pauses, the source captureStream stops emitting
    // new frames, so PanoRecorder.captureFrame() keeps drawing the latest
    // input (the frozen frame) for the duration of the pause — capturing any
    // drawings or PTZ adjustments the user makes over that frozen frame.
    this.startFrameDriver();
    this.isRecording.set(true);
  }

  async stopRecording() {
    if (!this.recorder) {
      this.resetRecordingUI();
      return;
    }
    const recorder = this.recorder;
    const format = this.recordingFormat();
    this.recorder = null;
    this.stopFrameDriver();
    if (this.recordingTickInterval) {
      clearInterval(this.recordingTickInterval);
      this.recordingTickInterval = null;
    }
    this.isRecording.set(false);
    try {
      const result = await recorder.stop();
      if (result && format) {
        recorder.download(result.blob, result.mimeType, format);
      }
    } catch (err) {
      console.error('Failed to finalize panorama recording', err);
    } finally {
      this.resetRecordingUI();
    }
  }

  private resetRecordingUI() {
    this.recordingFormat.set(null);
    this.recordingElapsedSec.set(0);
    this.recordingStartedAtMs = 0;
    this.viewer?.needsUpdate();
  }

  /**
   * Called from destroy. Releases the frame driver and tick interval
   * synchronously, then fire-and-forgets recorder.stop() — the MediaRecorder
   * finishes encoding asynchronously and the resulting blob is discarded
   * because the component is going away.
   */
  private abortRecording() {
    if (!this.recorder) return;
    const recorder = this.recorder;
    this.recorder = null;
    this.stopFrameDriver();
    if (this.recordingTickInterval) {
      clearInterval(this.recordingTickInterval);
      this.recordingTickInterval = null;
    }
    this.isRecording.set(false);
    recorder.stop().catch(() => {});
  }

  /**
   * Wait until PSV's internal WebGL canvas has resized itself to match the
   * (now aspect-locked) container. PSV detects the resize via ResizeObserver
   * but takes a few animation frames to actually reshape its canvas; until
   * that happens, any composite read from the canvas pulls the OLD viewport
   * shape, which gets stretched into the target output and produces a
   * distorted result. Bounded by a 500 ms timeout so we never block forever.
   */
  private async awaitViewerCanvasSettle(): Promise<void> {
    const containerEl = this.viewerContainerRef().nativeElement;
    const matches = () => {
      const canvas = containerEl.querySelector('canvas');
      if (!canvas) return false;
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      const ew = containerEl.clientWidth;
      const eh = containerEl.clientHeight;
      // Allow 1 px slop for sub-pixel rounding from CSS calc().
      return Math.abs(cw - ew) <= 1 && Math.abs(ch - eh) <= 1 && cw > 0 && ch > 0;
    };
    const deadline = performance.now() + 500;
    while (!matches() && performance.now() < deadline) {
      this.viewer?.needsUpdate();
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
    }
    // One extra rAF after match so PSV actually paints the new viewport size.
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
  }

  /**
   * rAF-paced output frame driver. Calls recorder.captureFrame() at exactly
   * the target FPS using schedule-based pacing, so on a 60 Hz display the
   * missed-vs-caught rAFs alternate in a 2-3 pattern (50 ms, 33 ms, 50 ms,
   * 33 ms…) and the average frame rate matches the target over any sustained
   * interval. Elapsed-since-last-push pacing would quantize 40 ms intervals
   * up to 50 ms on a 60 Hz display, yielding 20 fps when 25 was requested.
   */
  private startFrameDriver() {
    this.stopFrameDriver();
    this.recordingFrameDriverActive = true;
    const targetFps = this.fps() ?? 30;
    const targetIntervalMs = 1000 / targetFps;
    let nextPushMs = 0;
    const tick = (now: number) => {
      if (!this.recordingFrameDriverActive) return;
      if (nextPushMs === 0) {
        nextPushMs = now;
      }
      if (now >= nextPushMs) {
        nextPushMs += targetIntervalMs;
        this.recorder?.captureFrame();
      }
      this.recordingRafHandle = requestAnimationFrame(tick);
    };
    this.recordingRafHandle = requestAnimationFrame(tick);
  }

  private stopFrameDriver() {
    this.recordingFrameDriverActive = false;
    if (this.recordingRafHandle !== null) {
      cancelAnimationFrame(this.recordingRafHandle);
      this.recordingRafHandle = null;
    }
  }
  
  formatDuration(seconds: number): string {
    if (!seconds) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
  
  listenToFullscreenChange() {
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen.set(!!document.fullscreenElement);
      setTimeout(() => this.viewer?.needsUpdate(), 100);
    });
  }
  
  trackUserActivity() {
    const reset = () => {
      this.isUserActive.set(true);
      clearTimeout(this.userActivityTimeout);
      this.userActivityTimeout = setTimeout(() => {
        if (this.isPlaying()) this.isUserActive.set(false);
      }, 3000);
    };
    const wrapper = this.playerWrapperRef().nativeElement;
    wrapper.addEventListener('mousemove', reset);
    wrapper.addEventListener('click', reset);
    wrapper.addEventListener('touchstart', reset);
  }
}
